import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { FileText, Upload, Trash2, Download, Paperclip, Users, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function AdminClientDocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [filterClient, setFilterClient] = useState('all');
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [dRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/client-documents`),
        axios.get(`${API}/admin/clients`).catch(() => ({ data: [] })),
      ]);
      setDocs(dRes.data || []);
      setClients(cRes.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const visible = docs.filter((d) => filterClient === 'all' || d.client_id === filterClient);

  const toggleVisibility = async (doc) => {
    try {
      await axios.put(`${API}/admin/client-documents/${doc.id}/visibility`, { visible_to_staff: !doc.visible_to_staff });
      toast.success(!doc.visible_to_staff ? 'Now visible to assigned staff' : 'Hidden from staff');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const remove = async (doc) => {
    try {
      await axios.delete(`${API}/admin/client-documents/${doc.id}`);
      toast.success('Document deleted');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const download = async (doc) => {
    try {
      const res = await axios.get(`${API}/admin/client-documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.file_name || 'document'; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-4 max-w-4xl mx-auto">
        <div className="border-b border-border pb-4 flex items-center gap-3 flex-wrap">
          <FileText className="w-6 h-6 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground">CLIENT DOCUMENTS</h1>
            <p className="text-xs text-muted-foreground mt-1">RAMS, instructions and files shared between you and your clients.</p>
          </div>
          <Button
            onClick={() => setUploadOpen(true)}
            data-testid="admin-doc-upload-open"
            className="ml-auto bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm text-xs uppercase tracking-wider"
          >
            <Upload className="w-4 h-4 mr-1.5" /> Upload for Client
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground shrink-0">Client:</Label>
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="rounded-sm h-9 text-sm w-64 bg-background border-border" data-testid="doc-filter-client">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all">All clients</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground" data-testid="admin-docs-empty">No documents here yet.</p>
        ) : (
          <div className="space-y-2">
            {visible.map((d) => (
              <Card key={d.id} className="bg-card border-border rounded-sm" data-testid={`admin-doc-${d.id}`}>
                <CardContent className="p-3.5 flex items-center gap-3 flex-wrap sm:flex-nowrap">
                  <span className="w-9 h-9 rounded-sm bg-muted/40 border border-border flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-primary" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground truncate">{d.file_name}</p>
                    <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                      <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{d.company_name}</span>
                      {d.request_site && (
                        <Badge variant="outline" className="text-[10px] border-border">
                          <Paperclip className="w-2.5 h-2.5 mr-0.5" />{d.request_site}
                        </Badge>
                      )}
                      <span>{fmtSize(d.file_size || 0)} · {(() => { try { return format(parseISO(d.created_at), 'dd MMM yyyy HH:mm'); } catch { return ''; } })()}</span>
                      <Badge variant="outline" className={`text-[10px] ${d.uploaded_by === 'admin' ? 'border-primary/40 text-primary' : 'border-blue-500/40 text-blue-300'}`}>
                        {d.uploaded_by === 'admin' ? `Admin (${d.uploader_name})` : 'From client'}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {d.request_id && (
                      <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground cursor-pointer" title="Assigned staff can see this document">
                        <Users className="w-3.5 h-3.5" />
                        <Switch
                          checked={!!d.visible_to_staff}
                          onCheckedChange={() => toggleVisibility(d)}
                          data-testid={`doc-visibility-${d.id}`}
                        />
                        Staff
                      </label>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => download(d)} className="h-8 w-8 text-muted-foreground hover:text-primary" data-testid={`admin-download-doc-${d.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(d)} className="h-8 w-8 text-muted-foreground hover:text-red-400" data-testid={`admin-delete-doc-${d.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <UploadDialog open={uploadOpen} clients={clients} onClose={() => setUploadOpen(false)} onSaved={fetchAll} />
    </AdminLayout>
  );
}

function UploadDialog({ open, clients, onClose, onSaved }) {
  const [clientId, setClientId] = useState('');
  const [requests, setRequests] = useState([]);
  const [reqId, setReqId] = useState('general');
  const [visibleToStaff, setVisibleToStaff] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) { setClientId(''); setReqId('general'); setVisibleToStaff(false); setRequests([]); }
  }, [open]);

  useEffect(() => {
    if (!clientId) { setRequests([]); setReqId('general'); return; }
    axios.get(`${API}/admin/staffing-requests`)
      .then((res) => setRequests((res.data || []).filter((r) => r.client_id === clientId && r.status === 'active')))
      .catch(() => setRequests([]));
    setReqId('general');
  }, [clientId]);

  const upload = async (file) => {
    if (!file) return;
    if (!clientId) { toast.error('Select a client first'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('client_id', clientId);
      if (reqId !== 'general') fd.append('request_id', reqId);
      fd.append('visible_to_staff', visibleToStaff ? 'true' : 'false');
      fd.append('file', file);
      await axios.post(`${API}/admin/client-documents`, fd);
      toast.success('Document uploaded — client emailed');
      onClose();
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-card border-border rounded-sm" data-testid="admin-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-barlow tracking-widest uppercase text-primary text-lg">Upload for Client</DialogTitle>
          <DialogDescription>PDF, Word, JPG, PNG — max 10 MB. The client is emailed automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Client *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="rounded-sm h-9 text-sm bg-background border-border" data-testid="upload-client-select">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Attach to request</Label>
            <Select value={reqId} onValueChange={setReqId}>
              <SelectTrigger className="rounded-sm h-9 text-sm bg-background border-border" data-testid="upload-request-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="general">General (no request)</SelectItem>
                {requests.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.site_name} ({r.category})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {reqId !== 'general' && (
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <Switch checked={visibleToStaff} onCheckedChange={setVisibleToStaff} data-testid="upload-visible-staff" />
              Visible to assigned staff (they see it on their shift)
            </label>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="admin-doc-file-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading || !clientId}
            className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider text-xs"
            data-testid="admin-doc-upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading…' : 'Choose File & Upload'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
